<template>
  <section class="content-block">
    <div class="max-w-7xl mx-auto px-4 py-8">
      <div class="bg-white p-4">
        <div class="max-w-md mx-auto">
          <template v-if="error">
            <HRTSessionEnded
              :icon="content.iconSessionEnded"
              :text-denied="content.textSessionEnded"
              :text-footer="content.textFooter"
              @retry="handleRetry"
            />
          </template>
          <template v-else-if="isValidating || !validationResult">
            <div class="hrt-component">
              <div class="flex justify-center items-center h-[400px]">
                <ArrowPathIcon class="text-primary animate-spin w-32 h-32" />
              </div>
            </div>
          </template>
          <template v-else-if="isDenied">
            <HrtDenied
              :icon="content.iconDenied"
              :text-denied="content.textDenied"
              :text-footer="content.textFooter"
            />
          </template>
          <template v-else-if="isApproved">
            <HRTThankYou
              :icon="content.iconThankYou"
              :text-thank-you="content.textThankYou"
              :text-footer="content.textFooter"
              @ok="handleOk"
            />
          </template>
          <template v-else>
            <HRTLanding
              :icon="content.iconLanding"
              :text-landing="content.textLanding"
              :text-footer="content.textFooter"
              :auth-info="validationResult"
              @confirm="handleConfirm"
              @deny="handleDeny"
            />
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useBackendService } from "~/composables/services/useBackend.service";
import { useRouter } from "vue-router";
import type { IconType } from "../HRT/types";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";

interface AuthInfo {
  id: number;
  createdDate: string;
  email: string;
  customerId: number;
  transactionType: string;
  authType: string;
  authValidity: string;
  initiatedBy: string;
  sourcePlatform: string;
  emailSentAt: string;
  authStatus: string;
  resendTimer: number;
  requestFrom: string;
}

const props = defineProps<{
  content: {
    textDenied: string;
    textFooter: string;
    textLanding: string;
    textSessionEnded: string;
    textThankYou: string;
    iconDenied: IconType;
    iconLanding: IconType;
    iconSessionEnded: IconType;
    iconThankYou: IconType;
    token: string;
  };
}>();

const error = ref(false);
const isValidating = ref(true);
const isDenied = ref(false);
const isApproved = ref(false);
const validationResult = ref<AuthInfo | null>(null);
const {
  validateAuthorizationToken,
  approveAuthorizationToken,
  rejectAuthorizationToken,
} = useBackendService();
const router = useRouter();

const handleRetry = async () => {
  error.value = false;
  router.push("/account/dashboard?view=personal-details");
};

const handleConfirm = async () => {
  try {
    isValidating.value = true;
    const result = await approveAuthorizationToken({
      ...validationResult.value,
      authToken: props.content.token,
    });
    if (result?.body?.authStatus === "APPROVED") {
      isApproved.value = true;
    }
  } catch (err) {
    console.error("Error approving token:", err);
  } finally {
    isValidating.value = false;
  }
};

const handleDeny = async () => {
  try {
    isValidating.value = true;
    const result = await rejectAuthorizationToken({
      ...validationResult.value,
      authToken: props.content.token,
    });
    if (result?.body?.authStatus === "REJECTED") {
      isDenied.value = true;
    }
  } catch (err) {
    console.error("Error rejecting token:", err);
  } finally {
    isValidating.value = false;
  }
};

const handleOk = () => {
  router.push("/account");
};

const validateToken = async () => {
  try {
    const result = await validateAuthorizationToken(props.content.token);
    console.log("Token validation result:", result);
    validationResult.value = result.body;
    isValidating.value = false;
  } catch (err) {
    console.error("Error validating token:", err);
    error.value = true;
    isValidating.value = false;
  }
};

onMounted(() => {
  validateToken();
});
</script>

<style lang="scss" scoped>
@use "../HRT/styles/hrt.scss";

.content-block {
  @apply bg-white;
}

.loading-spinner {
  @apply animate-spin rounded-full h-8 w-8 border-b-2 border-primary;
}

.loading-text {
  @apply ml-2;
}
</style>
